'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var mplTokenMetadata = require('@metaplex-foundation/mpl-token-metadata');
var web3_js = require('@solana/web3.js');
var Authorization = require('../Authorization.cjs');
var Operation = require('../../../types/Operation.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');
var Metadata = require('../models/Metadata.cjs');

const TOKEN_AUTH_RULES_ID = new web3_js.PublicKey('auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg');

// -----------------
// Operation
// -----------------

const Key = 'UnlockNftOperation';

/**
 * Unlock a programmable NFT.
 *
 * ```ts
 * await metaplex.nfts().unlock({ nftOrSft, authority });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const unlockNftOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const unlockNftOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return unlockNftBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Unlock a programmable NFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .unlock({ nftOrSft, authority });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const unlockNftBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft
  } = params;

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const tokenProgram = metaplex.programs().getToken(programs);
  const systemProgram = metaplex.programs().getSystem(programs);

  // Auth.
  const auth = Authorization.parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: params.authority,
    authorizationDetails: params.authorizationDetails,
    programs
  });

  // PDAs.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: nftOrSft.address,
    programs
  });
  const edition = metaplex.nfts().pdas().masterEdition({
    mint: nftOrSft.address,
    programs
  });
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer)

  // Update the metadata account.
  .add({
    instruction: mplTokenMetadata.createUnlockInstruction({
      authority: auth.accounts.authority,
      tokenOwner: auth.accounts.approver,
      token: auth.accounts.token,
      mint: nftOrSft.address,
      metadata,
      edition: Metadata.isNonFungible(nftOrSft) ? edition : undefined,
      tokenRecord: auth.accounts.delegateRecord,
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: web3_js.SYSVAR_INSTRUCTIONS_PUBKEY,
      splTokenProgram: tokenProgram.address,
      authorizationRules: auth.accounts.authorizationRules,
      authorizationRulesProgram: TOKEN_AUTH_RULES_ID
    }, {
      unlockArgs: {
        __kind: 'V1',
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.instructionKey ?? 'UnlockNft'
  });
};

exports.unlockNftBuilder = unlockNftBuilder;
exports.unlockNftOperation = unlockNftOperation;
exports.unlockNftOperationHandler = unlockNftOperationHandler;
//# sourceMappingURL=unlockNft.cjs.map
